/**
 * Sales Settings Panel
 *
 * Displayed in the AppShell settings dialog. Contains the org switcher
 * and one-time report exports.
 */

import { useState } from 'react';
import {
  Button,
  Spinner,
  Divider,
  Caption1,
} from '@fluentui/react-components';
import { ArrowDownloadRegular } from '@fluentui/react-icons';
import { OrgSwitcher } from '@shared/providers';
import { COLORS } from '@shared/ui';
import { exportBrokerFirstWon, downloadCsv } from '@sales/features/broker-first-won-csv';

export function SalesSettings() {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportDone, setExportDone] = useState(false);

  const handleExportBrokerFirstWon = async () => {
    setExporting(true);
    setExportError(null);
    setExportDone(false);
    try {
      const csv = await exportBrokerFirstWon();
      const today = new Date().toISOString().split('T')[0];
      downloadCsv(csv, `broker-first-sold-opportunity-${today}.csv`);
      setExportDone(true);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <OrgSwitcher variant="light" />

      <Divider />

      <div>
        <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>Reports</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button
            appearance="secondary"
            icon={exporting ? <Spinner size="tiny" /> : <ArrowDownloadRegular />}
            onClick={handleExportBrokerFirstWon}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : 'Broker First Sold Opportunity'}
          </Button>
          {exportDone && (
            <Caption1 style={{ color: COLORS.success }}>Downloaded</Caption1>
          )}
          {exportError && (
            <Caption1 style={{ color: COLORS.error }}>{exportError}</Caption1>
          )}
        </div>
        <Caption1 style={{ color: COLORS.textMuted, display: 'block', marginTop: '6px' }}>
          CSV of each broker's first won opportunity with tenure, products, and group details.
        </Caption1>
      </div>
    </div>
  );
}
